import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Drawer from 'rc-drawer';

// Components
import { Button } from '../Button';
import { FocusTrap } from '../FocusTrap';
import { CloseTapTarget } from '../CloseTapTarget';
import { MaterialIcon } from '../MaterialIcon';
import { ConfirmationModal } from '../ConfirmationModal';

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 100vh;
`;

const DrawerRow = `
    padding: 1rem;
    display: flex;
    flex-direction: row;
`;

export const DrawerHeader = styled.div`
    background: ${({ headercolor }) => headercolor ?? '#6fa7d1'};
    color: white;
    font-weight: 600;
    ${DrawerRow};
    justify-content: space-between;
`;

export const DrawerFooter = styled.div`
    ${DrawerRow};
    justify-content: flex-end;
`;

export const DrawerContent = React.forwardRef(
    ({ children, closeFn, closeDrawer, title, onSave, onApply, saveDisabled, isLoading, headerColor, open }, ref) => {
        const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
        const [lastClicked, setLastClicked] = React.useState(null);
        const DrawerContainerRef = React.useRef(null);
        const Wrapper = React.useMemo(() => (open ? FocusTrap : React.Fragment[open]));

        React.useImperativeHandle(
            ref,
            () => ({
                setShowDiscardChangesModal: (newState) => {
                    setShowDiscardChangesModal(newState);
                },
                drawerFocus: () => {
                    DrawerContainerRef?.current?.focus?.();
                }
            }),
            [setShowDiscardChangesModal, DrawerContainerRef]
        );

        return (
            <>
                <Wrapper>
                    <Container tabIndex="0" ref={DrawerContainerRef} style={{ zIndex: 9990, background: '#fff' }}>
                        <DrawerHeader headercolor={headerColor}>
                            {title}
                            <CloseTapTarget onClick={closeFn} color="#fff">
                                <MaterialIcon icon={'close'} color="#fff" />
                            </CloseTapTarget>
                        </DrawerHeader>
                        <div style={{ overflowX: 'hidden' }}>{children}</div>
                        <DrawerFooter>
                            {/* <Button text appearance="primary" disabled={saveDisabled} onClick={onSave} loading={isLoading}>
                            OK
                        </Button> */}
                            <Button
                                text
                                appearance="primary"
                                disabled={saveDisabled}
                                tabIndex="0"
                                onClick={(data) => {
                                    setLastClicked('save');
                                    onSave(data);
                                    // setTimeout(() => {
                                    //     setIsSaving(false);
                                    // }, 0);
                                }}
                                loading={isLoading && lastClicked === 'save'}
                            >
                                Save
                            </Button>
                            <Button
                                text
                                appearance="secondary"
                                disabled={saveDisabled}
                                tabIndex="0"
                                onClick={(data) => {
                                    setLastClicked('apply');
                                    onApply(data);
                                    // setTimeout(() => {
                                    //     setIsApplying(false);
                                    // }, 0);
                                }}
                                loading={isLoading && lastClicked === 'apply'}
                            >
                                Apply
                            </Button>
                            <Button text appearance="secondary" onClick={closeFn}>
                                Cancel
                            </Button>
                        </DrawerFooter>
                    </Container>
                </Wrapper>
                {showDiscardChangesModal ? (
                    <ConfirmationModal
                        header={'Discard unsaved changes?'}
                        body={'You have unsaved changes which will be discarded upon closing. Do you wish to proceed?'}
                        confirmButtonText={'Yes, Discard Changes'}
                        confirmButtonProps={{ appearance: 'destructive', text: true }}
                        closeDialog={() => setShowDiscardChangesModal(false)}
                        onConfirm={() =>
                            new Promise((resolve) => {
                                closeDrawer();
                                resolve();
                            })
                        }
                    />
                ) : null}
            </>
        );
    }
);

DrawerContent.displayName = 'DrawerContent';

DrawerContent.propTypes = {
    children: PropTypes.node,
    closeFn: PropTypes.func,
    closeDrawer: PropTypes.func,
    title: PropTypes.string,
    saveDisabled: PropTypes.bool,
    onSave: PropTypes.func,
    onApply: PropTypes.func,
    isLoading: PropTypes.bool,
    headerColor: PropTypes.string,
    open: PropTypes.bool
};

export const DrawerContainer = Drawer;
